import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  BusinessRounded,
  MailOutline,
  VerifiedUser,
  LockReset
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { useNavigate } from 'react-router-dom';
import api from '../api';

const steps = ['Organisation', 'Account', 'Verify OTP', 'New password'];

const GradientBackground = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg,#f5f7fa 0%,#c3cfe2 100%)'
});

const GlassCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  width: 420,
  backdropFilter: 'blur(6px)',
  backgroundColor: 'rgba(255,255,255,0.85)'
}));

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const [org, setOrg] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [orgId, setOrgId] = useState();
  const [resetRequestToken, setResetRequestToken] = useState();
  const [resetToken, setResetToken] = useState();


  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);

  const next = () => setActiveStep(s => s + 1);
  const back = () => setActiveStep(s => s - 1);


  useEffect(() => {
    if (activeStep !== 2 || !expiresAt) return;
    const id = setInterval(() => {
      const sec = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setTimeLeft(sec);
      if (sec === 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [activeStep, expiresAt]);


  const handleOrgSubmit = async () => {
    if (!org.trim()) {
      setError('Please enter your organisation');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { data } = await api.post('forgot/check-org', { org });
      if (data.exists && data.companyId) {
        setSuccess('Organisation found.');
        setOrgId(data.companyId);
        next();
      } else {
        setError('Organisation not found. Please check the name and try again.');
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Error checking organisation');
    } finally {
      setLoading(false);
    }
  };


  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      setError('Enter your e-mail or username');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {

      // 1) Check user
      const { data: chk } = await api.post('forgot/check-user', { orgId, email });

      if (!chk.exists) {
        setError('No account found with that email/username in this organisation.');
        return;
      }

      // 2) Request reset token
      const { data: req } = await api.post('forgot/request-reset', { org, email });

      if (!req.resetRequestToken) {
        setSuccess('If an account exists, you will receive an e-mail.');
        return;
      }

      setResetRequestToken(req.resetRequestToken);

      // 3) Send OTP
      const { data: otpRes } = await api.post('forgot/send-otp', {
        resetRequestToken: req.resetRequestToken,
      });

      const expiresMs = new Date(otpRes.expiresAt).getTime();
      setExpiresAt(expiresMs);
      setTimeLeft(Math.ceil((expiresMs - Date.now()) / 1000));
      setSuccess('OTP sent! Check your inbox.');
      next();
    } catch (e) {
      console.error('[FP] handleEmailSubmit error', {
        error: e,
        responseStatus: e?.response?.status,
        responseData: e?.response?.data,
      });
      setError(e?.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };



  const handleOtpSubmit = async () => {
    if (otp.length !== 6) {
      setError('6-digit code required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post('forgot/verify-otp', { resetRequestToken, otp });

      if (!data?.resetToken) {
        setError("OTP verification failed.");
        return;
      }

      setResetToken(data.resetToken);
      next();
    } catch (e) {
      setError(e?.response?.data?.message || 'OTP invalid/expired');
    } finally {
      setLoading(false);
    }
  };


  const handlePasswordSubmit = async () => {
    if (password.length < 8) {
      setError('Min 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords don’t match');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post('forgot/change-password', { password, resetToken });
      setSuccess('Password changed – redirecting…');
      setTimeout(() => navigate('/login'), 1500);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };


  const ActionButton = ({ label, onClick, disabledExtra }) => (
    <Button
      variant="contained"
      fullWidth
      sx={{ mt: 2 }}
      onClick={onClick}
      disabled={loading || disabledExtra}
      endIcon={loading ? <CircularProgress size={20} /> : undefined}
    >
      {label}
    </Button>
  );


  const StepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <TextField
              fullWidth
              label="Organisation"
              value={org}
              onChange={e => setOrg(e.target.value)}
              autoFocus
            />
            <ActionButton label="Next" onClick={handleOrgSubmit} />
          </>
        );
      case 1:
        return (
          <>
            <TextField
              fullWidth
              label="E-mail / Username"
              value={email}
              type="email"
              onChange={e => setEmail(e.target.value)}
              autoFocus
            />
            <ActionButton label="Send OTP" onClick={handleEmailSubmit} />
          </>
        );
      case 2: {
        const m = String(Math.floor(timeLeft / 60)).padStart(2, '0');
        const s = String(timeLeft % 60).padStart(2, '0');
        const expired = timeLeft === 0;
        return (
          <>
            <TextField
              fullWidth
              label="6-digit OTP"
              value={otp}
              onChange={e => setOtp(e.target.value)}
              autoFocus
              disabled={expired}
            />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              {expired
                ? 'Code expired—click Resend to get a new one.'
                : `OTP expires in ${m}:${s}`}
            </Typography>
            <ActionButton
              label="Verify code"
              onClick={handleOtpSubmit}
              disabledExtra={expired}
            />
            {expired && (
              <Button
                size="small"
                onClick={handleEmailSubmit}
                sx={{ mt: 1 }}
                disabled={loading}
              >
                Resend OTP
              </Button>
            )}
          </>
        );
      }
      case 3:
        return (
          <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={e => {
              e.preventDefault();
              handlePasswordSubmit();
            }}
          >
            <TextField
              fullWidth
              label="New password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <TextField
              fullWidth
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              sx={{ mt: 2 }}
              autoComplete="new-password"
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
              endIcon={loading && <CircularProgress size={20} />}
            >
              Change password
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };


  return (
    <GradientBackground>
      <GlassCard elevation={3}>
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Typography variant="h5" align="center" fontWeight={600}>
              Forgot password
            </Typography>
          </Grid>

          <Grid item>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, idx) => (
                <Step key={label}>
                  <StepLabel
                    icon={[
                      <BusinessRounded />,
                      <MailOutline />,
                      <VerifiedUser />,
                      <LockReset />
                    ][idx]}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>

          {error && (
            <Grid item>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}

          <Grid item>{StepContent()}</Grid>

          {activeStep > 0 && activeStep < 3 && (
            <Grid item>
              <Button onClick={back} size="small">
                Back
              </Button>
            </Grid>
          )}
        </Grid>
      </GlassCard>
    </GradientBackground>
  );
}

